import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INVITE_ACCEPTED_STORAGE_KEY = 'siyak_invite_accepted';

const normalizeNextPath = (candidate) => {
  const value = String(candidate || '').trim();
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//')) return null;
  if (value.includes('\\')) return null;
  return value;
};

const getErrorMessage = (detail, fallback) => {
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item.msg === 'string') return item.msg;
        return null;
      })
      .filter(Boolean);
    if (messages.length > 0) return messages.join(', ');
  }

  if (detail && typeof detail.msg === 'string') {
    return detail.msg;
  }

  return fallback;
};

export const Login = () => {
  useEffect(() => { document.title = 'Sign In - SIYAK'; }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshUser, sessionExpired, clearSessionExpired } = useAuth();
  const queryParams = new URLSearchParams(location.search);
  const inviteToken = queryParams.get('invite_token');
  const inviteEmail = queryParams.get('invite_email') || '';
  const nextPath = normalizeNextPath(queryParams.get('next'));
  
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';
  const getSafeFallbackPath = () => {
    if (!from) return '/';
    if (from.startsWith('/admin') || from.startsWith('/landlord') || from.startsWith('/tenant')) {
      return '/';
    }
    return from;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);

      const normalizedRole = String(user?.role || '').toLowerCase();
      let inviteAccepted = false;
      let acceptedPropertyTitle = null;

      if (inviteToken && normalizedRole === 'tenant') {
        try {
          const acceptResponse = await axios.post(`${API_URL}/api/invites/accept`, { token: inviteToken });
          inviteAccepted = acceptResponse.data?.status === 'accepted';
          acceptedPropertyTitle = acceptResponse.data?.property_title || null;
          toast.success('Invite accepted. Opening your tenancy dashboard.');
        } catch (inviteError) {
          const detail = inviteError.response?.data?.detail;
          if (detail) {
            toast.error(detail);
          } else {
            toast.error('Logged in, but invite acceptance failed');
          }
        } finally {
          const refreshedUser = await refreshUser(true);
          if (inviteAccepted) {
            try {
              sessionStorage.setItem(INVITE_ACCEPTED_STORAGE_KEY, JSON.stringify({
                propertyTitle: acceptedPropertyTitle || refreshedUser?.property_title || 'your property',
                acceptedAt: new Date().toISOString(),
              }));
            } catch (error) {}
          }
        }
      }

      if (inviteAccepted && normalizedRole === 'tenant') {
        navigate('/tenant', { replace: true });
        return;
      }

      if (nextPath) {
        navigate(nextPath, { replace: true });
        return;
      }
      
      // Redirect based on role
      switch (normalizedRole) {
        case 'admin':
          navigate('/admin', { replace: true });
          break;
        case 'landlord':
          navigate('/landlord', { replace: true });
          break;
        case 'tenant':
          navigate('/tenant', { replace: true });
          break;
        default:
          navigate(getSafeFallbackPath(), { replace: true });
      }
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-navy-50 px-4 py-12" data-testid="login-page">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
              <Building2 className="h-10 w-10 text-royal-600" />
            </Link>
            <CardTitle className="font-heading text-2xl">Welcome Back</CardTitle>
            <CardDescription>
              Sign in to your SIYAK account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessionExpired && (
              <div className="mb-4 p-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="session-expired-banner">
                Your session has expired. Please sign in again.
                <button onClick={clearSessionExpired} className="ml-2 underline text-amber-700 hover:text-amber-900">Dismiss</button>
              </div>
            )}
            {inviteToken && (
              <div className="mb-4 p-4 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm" data-testid="invite-login-banner">
                You were invited to join a property. Sign in with your tenant account to accept the invite.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-royal-600 hover:underline">
                Forgot your password?
              </Link>
            </div>

            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/register" className="text-royal-600 hover:underline font-medium">
                Register
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
